import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Dropdown, ElasticModal } from 'basic-components';
import { globalActions } from 'primary-components';

import { creatorSeriesActions } from 'actions/series';

import Row from './Row';

import { When } from 'react-if';

import './SymbolModal.scss';

const EMPTY_ARRAY = []
const SymbolModal = ({ index, onClose, symbol: symbolLocal, optionalText, withSourceSelect, onSubmit }) => {
  const dispatch = useDispatch(),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols) || EMPTY_ARRAY,
        sourcesRaw = useSelector(state => state.globalState.sources) || EMPTY_ARRAY,
        localSource = useSelector(state => state.globalState.localSource),
        [source, setSource] = useState(localSource),
        symbol = useMemo(() => symbolLocal || selectedSymbols[index], [index, selectedSymbols, symbolLocal]),
        isPattern = useMemo(() => symbol.pattern === "true" || symbol.pattern === undefined, [symbol.pattern]),
        optionalTextLocal = useMemo(() => optionalText || (isPattern ? undefined : `In group: ${symbol.groupName}`), [isPattern, optionalText, symbol]),
        title = useMemo(() =>  isPattern ? "Pattern information" : "Symbol information", [isPattern]),
        sources = useMemo(() => sourcesRaw.map(source => ({ label: source.readableName, value: source.name })), [sourcesRaw]),
        selectedSource = useMemo(() => selectedSymbols?.length > 0 ? selectedSymbols[0]?.source : undefined, [selectedSymbols]),
        onSubmitLocal = useCallback(() => {
          if(selectedSource && source !== selectedSource) {
            dispatch(globalActions.getToastsStore()).warning(creatorSeriesActions.PROVIDER_MESSAGE, "Discard selection", {
              withCancel: false,
              confirmText: "Discard all",
              confirmAction: () => {
                dispatch(creatorSeriesActions.onChange([], "selectedSymbols"))
                onSubmit(source)
                onClose()
              }
            })
          } else {
            onSubmit(source)
            onClose()
          }
        }, [dispatch, onClose, onSubmit, selectedSource, source]),
        footer = useMemo(() => (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
                onClick={onClose}
                size="small"
                variant="secondary" 
            >
              Cancel
            </Button>
            <Button
                disabled={!source}
                onClick={onSubmitLocal}
                size="small"
            >
              Create
            </Button>
          </div>
        ), [onClose, onSubmitLocal, source])

  return (
    <ElasticModal
        className="symbol-modal"
        footer={withSourceSelect ? footer : undefined}
        onClose={onClose}
        optionalText={optionalTextLocal}
        title={title}
    >
      <When condition={Object.keys(symbol?.symbols || {}).length > 0}>
        {() => (
          <Fragment>
            <div className="symbol-modal__header">
              Keys:
            </div>
            {Object.entries(symbol?.symbols || {}).map(([key, value]) => (
              <Row
                  key={`symbol-modal_symbol_row_${key}_${value}`}
                  label={key}
                  value={value}
              />
            ))}
          </Fragment>
        )}
      </When>
      <When condition={Object.keys(symbol?.metadata || {}).length > 0}>
        {() => (
          <Fragment>
            <div className="symbol-modal__header">
              Metadata:
            </div>
            {Object.entries(symbol?.metadata || {}).map(([key, value]) => (
              <Row
                  key={`symbol-modal_md_row_${key}_${value}`}
                  label={key}
                  value={value}
              />
            ))}
          </Fragment>
        )}
      </When>
      <When condition={(isPattern && (!!symbol?.query || !!symbol?.groupName)) || (!!symbol?.source || withSourceSelect)}>
        {() => (
          <Fragment>
            <div className="symbol-modal__header">
              Details:
            </div>
            <When condition={isPattern && !!symbol?.query}>
              {() => (
                <Row
                    key={`symbol-modal_details_row_query_${symbol.query}`}
                    label="Keyword"
                    value={symbol.query}
                />
              )}
            </When>
            <When condition={isPattern && !!symbol?.groupName}>
              {() => (
                <Row
                    key={`symbol-modal_details_row_group_name_${symbol.groupName}`}
                    label="Group name"
                    value={symbol.groupName}
                />
              )}
            </When>
            <When condition={!!symbol?.source}>
              {() => (
                <Row
                    key={`symbol-modal_details_row_source_${symbol.source}`}
                    label="Source"
                    value={symbol.source}
                />
              )}
            </When>
            <When condition={withSourceSelect}>
              {() => (
                <Row
                    isInput
                    key={`symbol-modal_details_row_source`}
                    label="Source"
                    value={(
                      <Dropdown
                          defaultValue={localSource}
                          fixed
                          name="source"
                          noError
                          onChange={setSource}
                          options={sources}
                      />
                    )}
                />
              )}
            </When>
          </Fragment>
        )}
      </When>
    </ElasticModal>
  )
}

SymbolModal.defaultProps = {
  index: undefined,
  optionalText:undefined,
  onSubmit: () => {},
  symbol: undefined,
  withSourceSelect: false
}

SymbolModal.propTypes = {
  index: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  optionalText: PropTypes.string,
  symbol: PropTypes.object,
  withSourceSelect: PropTypes.bool,
}

export default memo(SymbolModal);